/**
 * Preferences. They change defaults and density only — nothing is gated behind them — so
 * a power user gets the same app with more on screen, and a newcomer is never locked out
 * of a feature by a mode they did not know they were in.
 *
 * Saved to the signed-in account (Phase 7: `GET/PUT /api/prefs`), so they follow the
 * person from the office to the pool machine. This browser keeps a copy, which is what
 * the first paint uses and what applies while the server cannot be reached; a change
 * made then is sent once it can be. Last write wins, by the time of the change: the
 * server refuses a write based on an older copy than the one it holds (409), and the
 * newer of the two is kept.
 *
 * Wrapped in try/catch throughout: a private window may refuse storage, which must not
 * break the page.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { DEFAULT_PREFS, sanitizePrefs, type Prefs, type PrefsResponse } from '@studio/shared';
import { ApiError, prefsApi } from './api';
import { useAuth } from './auth';
import { noteSignedIn, noteSignedOut } from './offline/names';
import { localized } from './i18n';

export type { Prefs } from '@studio/shared';

const DEFAULTS: Prefs = DEFAULT_PREFS;
const KEY = 'studio.prefs';
/** what the copy above is based on: whose it is, the server's time for it, unsent changes */
const META_KEY = 'studio.prefs.sync';

export const PREFS_T = localized('preferences', {
  en: {
    savedToAccount: 'saved to your account',
    savedLocally: 'saved in this browser; sent to your account when the server can be reached',
    saving: 'saving…',
  },
  de: {
    savedToAccount: 'in deinem Konto gespeichert',
    savedLocally: 'in diesem Browser gespeichert; geht an dein Konto, sobald der Server erreichbar ist',
    saving: 'wird gespeichert…',
  },
});

interface SyncMeta {
  userId: number | null;
  /** the server's `updatedAt` of the copy this browser's is based on */
  base: string | null;
  /** when this browser changed a preference the server has not stored yet (ISO), or null */
  changedAt: string | null;
}

function load(): Prefs {
  try {
    const raw = window.localStorage.getItem(KEY);
    return raw ? { ...DEFAULTS, ...sanitizePrefs(JSON.parse(raw)) } : DEFAULTS;
  } catch {
    return DEFAULTS;
  }
}

function loadMeta(): SyncMeta {
  try {
    const raw = window.localStorage.getItem(META_KEY);
    const m = raw ? (JSON.parse(raw) as Partial<SyncMeta>) : {};
    return {
      userId: typeof m.userId === 'number' ? m.userId : null,
      base: typeof m.base === 'string' ? m.base : null,
      changedAt: typeof m.changedAt === 'string' ? m.changedAt : null,
    };
  } catch {
    return { userId: null, base: null, changedAt: null };
  }
}

function saveMeta(meta: SyncMeta): void {
  try {
    window.localStorage.setItem(META_KEY, JSON.stringify(meta));
  } catch {
    /* storage refused; the next load syncs from the server again */
  }
}

/** `account`: the server has these; `local`: it could not be reached, a change waits; `saving` */
export type PrefsSyncStatus = 'account' | 'local' | 'saving';

interface PrefsContext {
  prefs: Prefs;
  set: <K extends keyof Prefs>(key: K, value: Prefs[K]) => void;
  /** "Expert mode": every power-user preference on at once, or all back to defaults */
  setExpert: (on: boolean) => void;
  expert: boolean;
  /** where the preferences are saved, for the Preferences panel to say */
  status: PrefsSyncStatus;
}

const Ctx = createContext<PrefsContext | null>(null);

/** The label for where preferences are saved. */
export const prefsSavedLabel = (status: PrefsSyncStatus): string =>
  status === 'account' ? PREFS_T.savedToAccount : status === 'saving' ? PREFS_T.saving : PREFS_T.savedLocally;

export function PrefsProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [prefs, setPrefs] = useState<Prefs>(load);
  const [status, setStatus] = useState<PrefsSyncStatus>('account');
  const metaRef = useRef<SyncMeta>(loadMeta());
  const prefsRef = useRef(prefs);
  prefsRef.current = prefs;
  const userId = user?.id ?? null;
  const userRef = useRef<number | null>(userId);
  userRef.current = userId;

  useEffect(() => {
    try {
      window.localStorage.setItem(KEY, JSON.stringify(prefs));
    } catch {
      /* storage refused; the preference simply does not persist in this browser */
    }
    document.documentElement.dataset.density = prefs.density;
  }, [prefs]);

  const adopt = useCallback((res: PrefsResponse, forUser: number) => {
    metaRef.current = { userId: forUser, base: res.updatedAt, changedAt: null };
    saveMeta(metaRef.current);
    setPrefs({ ...DEFAULTS, ...res.prefs });
    setStatus('account');
  }, []);

  /**
   * Send this browser's preferences; `force` overwrites a newer stored copy (ours is newer
   * still). One request at a time: a second one based on the copy the first replaces
   * would be refused as stale.
   */
  const sendNow = useCallback(
    async (forUser: number, force: boolean): Promise<void> => {
      setStatus('saving');
      const startedFrom = metaRef.current.changedAt;
      try {
        const res = await prefsApi.put(sanitizePrefs(prefsRef.current), force ? null : metaRef.current.base);
        if (userRef.current !== forUser) return;
        // a change made while the request was out is still waiting for the next one
        const changedSince = metaRef.current.changedAt !== startedFrom;
        metaRef.current = { userId: forUser, base: res.updatedAt, changedAt: changedSince ? metaRef.current.changedAt : null };
        saveMeta(metaRef.current);
        setStatus('account');
      } catch (err) {
        if (userRef.current !== forUser) return;
        if (err instanceof ApiError && err.status === 409 && err.body?.updatedAt !== undefined) {
          const stored = { prefs: sanitizePrefs(err.body.prefs), updatedAt: err.body.updatedAt as string | null };
          const mine = metaRef.current.changedAt;
          // last write wins: the change made here is newer than the stored one, or not
          if (mine && stored.updatedAt && mine > stored.updatedAt) await sendNow(forUser, true);
          else adopt(stored, forUser);
          return;
        }
        // unreachable or refused: kept here, sent on the next load or change
        setStatus('local');
      }
    },
    [adopt],
  );
  const queue = useRef<Promise<void>>(Promise.resolve());
  const push = useCallback(
    (forUser: number, force = false): Promise<void> => {
      queue.current = queue.current.then(() => sendNow(forUser, force));
      return queue.current;
    },
    [sendNow],
  );

  /*
   * After sign-in: the account's preferences, or this browser's sent to the account.
   * Someone else's copy left in this browser (a shared computer) is not theirs to keep.
   */
  useEffect(() => {
    if (userId === null) return;
    noteSignedIn(userId);
    let live = true;
    const meta = metaRef.current;
    if (meta.userId !== null && meta.userId !== userId) {
      metaRef.current = { userId, base: null, changedAt: null };
      saveMeta(metaRef.current);
      setPrefs(DEFAULTS);
    }
    prefsApi
      .get()
      .then((res) => {
        if (!live) return;
        const m = metaRef.current;
        const changedHere = m.userId === userId && m.changedAt;
        if (changedHere && (!res.updatedAt || m.changedAt! > res.updatedAt)) {
          void push(userId, true);
        } else if (res.updatedAt) {
          adopt(res, userId);
        } else if (m.userId === null) {
          // nothing stored for this account yet: this browser's become the account's
          metaRef.current = { userId, base: null, changedAt: null };
          void push(userId, true);
        } else {
          setStatus('account');
        }
      })
      .catch(() => live && setStatus('local'));
    return () => {
      live = false;
    };
  }, [userId, push, adopt]);

  /*
   * An explicit sign-out clears the sheet copies kept for offline editing; a session
   * that merely expired (a 401 somewhere) keeps them for the same person's return.
   */
  const expired = useRef(false);
  useEffect(() => {
    const onExpired = () => {
      expired.current = true;
    };
    window.addEventListener('studio:unauthenticated', onExpired);
    return () => window.removeEventListener('studio:unauthenticated', onExpired);
  }, []);
  const previousUser = useRef<number | null>(userId);
  useEffect(() => {
    const was = previousUser.current;
    previousUser.current = userId;
    if (userId !== null) {
      expired.current = false;
      return;
    }
    if (was !== null && !expired.current) void noteSignedOut();
  }, [userId]);

  // a change waiting for the server goes when the browser is back online
  useEffect(() => {
    const onOnline = () => {
      const id = userRef.current;
      if (id !== null && metaRef.current.changedAt) void push(id);
    };
    window.addEventListener('online', onOnline);
    return () => window.removeEventListener('online', onOnline);
  }, [push]);

  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const update = useCallback(
    (fn: (p: Prefs) => Prefs) => {
      setPrefs((p) => {
        const next = fn(p);
        prefsRef.current = next;
        return next;
      });
      metaRef.current = { ...metaRef.current, userId: userRef.current ?? metaRef.current.userId, changedAt: new Date().toISOString() };
      saveMeta(metaRef.current);
      const id = userRef.current;
      if (id === null) return;
      // one request for a burst of clicks (Expert mode flips four at once)
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => {
        timer.current = null;
        void push(id);
      }, 400);
    },
    [push],
  );

  const set = useCallback(
    <K extends keyof Prefs>(key: K, value: Prefs[K]) => update((p) => ({ ...p, [key]: value })),
    [update],
  );
  const expert =
    prefs.density === 'compact' && prefs.expandAdvanced && prefs.technicalDetails && prefs.sourceFirst;
  const setExpert = useCallback(
    (on: boolean) =>
      update((p) =>
        on
          ? { ...p, density: 'compact', expandAdvanced: true, technicalDetails: true, sourceFirst: true }
          : { ...p, density: 'comfortable', expandAdvanced: false, technicalDetails: false, sourceFirst: false },
      ),
    [update],
  );

  const value = useMemo(() => ({ prefs, set, setExpert, expert, status }), [prefs, set, setExpert, expert, status]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function usePrefs(): PrefsContext {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('usePrefs outside PrefsProvider');
  return ctx;
}
